import { z } from "zod";

// ---------------------------------------------------------------------------
// Store measures (GRA-97 / GRA-84)
//
// Storage layout:
//   Businesses/{businessId}/kpiMeasureValues/{date}_{teamId}_{measure}
//
// A grocery store's numbers never belong to a person. Report uploads store
// every parsed column per day and per team: the store is a root sales team,
// departments are its child teams. Only *base* measures are stored. Derived
// measures (percentages, average purchase) are ratios computed from summed
// bases at read time, so a week's svinn % is total svinn ÷ total sales, never
// an average of daily percentages.
// ---------------------------------------------------------------------------

export const STORE_MEASURE_UNITS = ["kr", "count", "kg", "pct"] as const;
export type StoreMeasureUnit = (typeof STORE_MEASURE_UNITS)[number];

export const BASE_STORE_MEASURES = [
  "sales",
  "margin_kr",
  "margin_after_svinn_kr",
  "svinn_kr",
  "svinn_qty",
  "svinn_frequency",
  "line_items",
  "customer_visits",
  "margin_base_kr",
] as const;
export type BaseStoreMeasure = (typeof BASE_STORE_MEASURES)[number];

export const DERIVED_STORE_MEASURES = [
  "margin_pct",
  "margin_after_svinn_pct",
  "svinn_pct_sales",
  "svinn_pct_margin",
  "avg_purchase",
] as const;
export type DerivedStoreMeasure = (typeof DERIVED_STORE_MEASURES)[number];

export const STORE_MEASURES = [
  ...BASE_STORE_MEASURES,
  ...DERIVED_STORE_MEASURES,
] as const;
export type StoreMeasure = (typeof STORE_MEASURES)[number];
export const StoreMeasureSchema = z.enum(STORE_MEASURES);

interface MeasureCommon {
  /** Swedish label, as a store manager would say it. */
  label: string;
  unit: StoreMeasureUnit;
  /** "down" = lower is better (svinn). */
  direction: "up" | "down";
}

export interface BaseMeasureSpec extends MeasureCommon {
  kind: "base";
  /**
   * Whether department values add up to the store's. Customer visits and
   * svinn frequency do not: one customer visits several departments. A store
   * total for these exists only when the report has a total row.
   */
  departmentAdditive: boolean;
}

export interface DerivedMeasureSpec extends MeasureCommon {
  kind: "derived";
  numerator: BaseStoreMeasure;
  denominator: BaseStoreMeasure;
  /** Multiplier on numerator/denominator: 100 for percentages. */
  scale: number;
}

export type StoreMeasureSpec = BaseMeasureSpec | DerivedMeasureSpec;

export const STORE_MEASURE_CATALOGUE: Record<StoreMeasure, StoreMeasureSpec> = {
  sales: {
    kind: "base",
    label: "Försäljning",
    unit: "kr",
    direction: "up",
    departmentAdditive: true,
  },
  margin_kr: {
    kind: "base",
    label: "Bruttovinst (marginal kr)",
    unit: "kr",
    direction: "up",
    departmentAdditive: true,
  },
  margin_after_svinn_kr: {
    kind: "base",
    label: "Marginal efter svinn",
    unit: "kr",
    direction: "up",
    departmentAdditive: true,
  },
  svinn_kr: {
    kind: "base",
    label: "Svinn",
    unit: "kr",
    direction: "down",
    departmentAdditive: true,
  },
  svinn_qty: {
    kind: "base",
    label: "Svinn antal/kg",
    unit: "kg",
    direction: "down",
    departmentAdditive: true,
  },
  svinn_frequency: {
    kind: "base",
    label: "Svinnfrekvens",
    unit: "count",
    direction: "down",
    departmentAdditive: false,
  },
  line_items: {
    kind: "base",
    label: "Antal poster",
    unit: "count",
    direction: "up",
    departmentAdditive: true,
  },
  customer_visits: {
    kind: "base",
    label: "Kundbesök",
    unit: "count",
    direction: "up",
    departmentAdditive: false,
  },
  /**
   * The sales that margin % is computed on. ICA's "Marginal %" divides by
   * sales without deposits (pant), so margin kr ÷ "Försäljning inkl. pant"
   * reads 25.19 % where ICA says 25.42 %. Imports back it out of the printed
   * margin % (margin kr ÷ margin %), or use sales when there is none. Kronor,
   * so it sums over days like any base; never shown on its own.
   */
  margin_base_kr: {
    kind: "base",
    label: "Försäljning (marginalunderlag)",
    unit: "kr",
    direction: "up",
    departmentAdditive: true,
  },
  margin_pct: {
    kind: "derived",
    label: "Marginal %",
    unit: "pct",
    direction: "up",
    numerator: "margin_kr",
    denominator: "margin_base_kr",
    scale: 100,
  },
  margin_after_svinn_pct: {
    kind: "derived",
    label: "Marginal % efter svinn",
    unit: "pct",
    direction: "up",
    numerator: "margin_after_svinn_kr",
    denominator: "margin_base_kr",
    scale: 100,
  },
  svinn_pct_sales: {
    kind: "derived",
    label: "Svinn % av försäljning",
    unit: "pct",
    direction: "down",
    numerator: "svinn_kr",
    denominator: "sales",
    scale: 100,
  },
  svinn_pct_margin: {
    kind: "derived",
    label: "Svinn % av marginal",
    unit: "pct",
    direction: "down",
    numerator: "svinn_kr",
    denominator: "margin_kr",
    scale: 100,
  },
  avg_purchase: {
    kind: "derived",
    label: "Snittköp",
    unit: "kr",
    direction: "up",
    numerator: "sales",
    denominator: "customer_visits",
    scale: 1,
  },
};

/** Measures a store picks KPIs from; internal bases are left out. */
export const SELECTABLE_STORE_MEASURES: StoreMeasure[] = STORE_MEASURES.filter(
  (m) => m !== "margin_base_kr",
);

export function isBaseStoreMeasure(m: string): m is BaseStoreMeasure {
  return (BASE_STORE_MEASURES as readonly string[]).includes(m);
}

export function isStoreMeasure(m: string): m is StoreMeasure {
  return (STORE_MEASURES as readonly string[]).includes(m);
}

/** Base measures a measure's value is computed from. */
export function baseMeasuresOf(measure: StoreMeasure): BaseStoreMeasure[] {
  const spec = STORE_MEASURE_CATALOGUE[measure];
  return spec.kind === "base" ? [measure as BaseStoreMeasure] : [spec.numerator, spec.denominator];
}

// ---------------------------------------------------------------------------
// Stored values
// ---------------------------------------------------------------------------

/** One day's value of one base measure for one team (store or department). */
export interface KpiMeasureValueRecord {
  id: string;
  businessId: string;
  /** Report date, YYYY-MM-DD. */
  date: string;
  /** Sales team the value belongs to: the store root team or a department. */
  teamId: string;
  /** The store (root team) the value rolls up to. Equals `teamId` for store rows. */
  storeTeamId: string;
  measure: BaseStoreMeasure;
  actual: number;
  /** Same day last year as the report compares it (actual − ±); null if unknown. */
  lastYear: number | null;
  importId: string;
  updatedAt: string | null;
  /**
   * GRA-162: set once, on the first long cumulative upload for a given start
   * date — the last day (inclusive) `actual`/`lastYear` cover. Frozen forever
   * after that: later uploads sharing this row's date extend the chain via
   * `chainTotal`/`chainThrough` below, never by rewriting this row's own
   * period-to-date total. No other row is ever written inside
   * [date, cumulativePeriodEnd], so this one sums like any ordinary day.
   */
  cumulativePeriodEnd?: string | null;
  /**
   * GRA-162: internal bookkeeping for diffing the *next* upload that shares
   * this start date — never read by `aggregateMeasure` or shown anywhere.
   * The latest known cumulative total/last year and the day through which
   * it's known, updated on every upload in the chain (unlike the frozen
   * `actual`/`cumulativePeriodEnd` above).
   */
  chainTotal?: number | null;
  chainLastYear?: number | null;
  chainThrough?: string | null;
}

export function kpiMeasureValueDocId(
  date: string,
  teamId: string,
  measure: BaseStoreMeasure,
): string {
  return `${date}_${teamId}_${measure}`;
}

export const ListKpiMeasureValuesInput = z.object({
  businessId: z.string().min(1),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Exclusive. */
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  storeTeamId: z.string().min(1).optional(),
});
export type ListKpiMeasureValuesInput = z.infer<typeof ListKpiMeasureValuesInput>;

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

export interface MeasureAggregate {
  /** Null when nothing was recorded, or a ratio's denominator is zero. */
  actual: number | null;
  /** Null when last year is unknown for any contributing day. */
  lastYear: number | null;
  /** Distinct days with at least one contributing value. */
  days: number;
}

/**
 * Value of `measure` for one team over [from, to) (dates YYYY-MM-DD, `to`
 * exclusive). Uses only the team's own rows: a store total is the store root
 * team's rows, never a sum of its departments, because some measures don't
 * add up across departments. Kronor and counts are summed over days; ratios
 * are computed from the summed bases.
 *
 * A row carrying a frozen cumulative-period total (GRA-162, `actual` for
 * [date, cumulativePeriodEnd]) sums in exactly like any ordinary day: no
 * other row is ever written for the days inside that span, so there is
 * nothing to double-count, and omitting it would wrongly erase that whole
 * period's kronor from every read that covers it.
 */
export function aggregateMeasure(
  values: Pick<KpiMeasureValueRecord, "date" | "teamId" | "measure" | "actual" | "lastYear">[],
  measure: StoreMeasure,
  teamIds: string | string[],
  from: string,
  to: string,
): MeasureAggregate {
  const teams = new Set(Array.isArray(teamIds) ? teamIds : [teamIds]);
  const inRange = values.filter(
    (v) => teams.has(v.teamId) && v.date >= from && v.date < to,
  );
  const sumOf = (base: BaseStoreMeasure) => {
    const rows = inRange.filter((v) => v.measure === base);
    if (rows.length === 0) return { actual: null, lastYear: null, days: new Set<string>() };
    const actual = rows.reduce((s, r) => s + r.actual, 0);
    const lastYear = rows.every((r) => r.lastYear != null)
      ? rows.reduce((s, r) => s + (r.lastYear as number), 0)
      : null;
    return { actual, lastYear, days: new Set(rows.map((r) => r.date)) };
  };

  const spec = STORE_MEASURE_CATALOGUE[measure];
  if (spec.kind === "base") {
    const s = sumOf(measure as BaseStoreMeasure);
    return { actual: s.actual, lastYear: s.lastYear, days: s.days.size };
  }
  const num = sumOf(spec.numerator);
  const den = sumOf(spec.denominator);
  const ratio = (n: number | null, d: number | null) =>
    n == null || d == null || d === 0 ? null : roundMeasure((n / d) * spec.scale);
  const days = new Set([...num.days].filter((d) => den.days.has(d)));
  return {
    actual: ratio(num.actual, den.actual),
    lastYear: ratio(num.lastYear, den.lastYear),
    days: days.size,
  };
}

/** Six decimals, like calculated KPIs: enough for display, stable for tests. */
export function roundMeasure(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/**
 * Target templates a grocery store starts from (GRA-87): what the pilots
 * measure. Returned as KPI suggestions in place of the AI's sales ones.
 */
export const DVH_KPI_TEMPLATES = [
  {
    name: "Svinn",
    unit: "%",
    direction: "down" as const,
    target: 3,
    measure: "svinn_pct_sales" as const,
    targetKind: "absolute" as const,
    changePct: null,
    rationale: "Svinnet räknat på veckans summerade kronor, mot ett mål på högst 3 % av försäljningen.",
  },
  {
    name: "Bruttovinst",
    unit: "kr",
    direction: "up" as const,
    target: null,
    measure: "margin_kr" as const,
    targetKind: "vs_last_year" as const,
    changePct: 3,
    rationale: "Bruttovinsten mot samma period förra året plus 3 %, så att inflationen räknas in.",
  },
  {
    name: "Försäljning",
    unit: "kr",
    direction: "up" as const,
    target: null,
    measure: "sales" as const,
    targetKind: "vs_last_year" as const,
    changePct: 0,
    rationale: "Försäljningen mot samma period förra året.",
  },
  {
    name: "Snittköp",
    unit: "kr",
    direction: "up" as const,
    target: null,
    measure: "avg_purchase" as const,
    targetKind: "vs_last_year" as const,
    changePct: 2,
    rationale: "Snittköpet (försäljning delat på kundbesök) mot samma period förra året plus 2 %.",
  },
  {
    name: "Antal kunder",
    unit: "count",
    direction: "up" as const,
    target: null,
    measure: "customer_visits" as const,
    targetKind: "vs_last_year" as const,
    changePct: 0,
    rationale: "Antal kundbesök mot samma period förra året.",
  },
];
